/* Copyright (c) 2020-present CloudIO Inc */

import { pageAtom } from 'designer/atoms';
import {
  AnalyzeContext,
  AnalyzeIssue,
  AnalyzeResult,
} from 'layout/Sidebar/panels/TreeView/AnalyzeUtils';
import { itemWithDefaultValues } from 'registry/Components';
import { Item, RequiredItem } from '../../../../registry/Component.type';
import { isNull } from 'utils/isEmpty';

export async function analyzeStringColumn({
  platform,
  appUid,
  pageId,
  itemId,
  mgr,
}: AnalyzeContext): Promise<AnalyzeResult> {
  const alerts: AnalyzeIssue[] = [];
  const autoFixes: (() => void)[] = [];
  const page = platform.getPage(appUid, pageId);
  if (!page) {
    return null;
  }
  const store = platform.getParentStore(appUid, pageId, itemId);
  const item = itemWithDefaultValues(
    page[itemId],
  ) as RequiredItem<'StringColumn'>;
  const { viewAttribute, label, maxLength, required, insertable, updatable } =
    item.props;
  const isLOV = !!platform.findParentOfType(appUid, pageId, itemId, 'LOVGrid');

  if (!store) {
    alerts.push({
      key: `incomplete-${itemId}`,
      showItemId: itemId,
      alert: `Incomplete ${mgr.name}. Must be wrapped inside a Data Store Reference!`,
      itemId,
    });
    return [alerts, autoFixes];
  }
  if (!viewAttribute) {
    alerts.push({
      key: `incomplete-${itemId}`,
      showItemId: itemId,
      alert: `Incomplete ${mgr.name} [${label}]. Missing view attribute!`,
      itemId,
    });
    return [alerts, autoFixes];
  }
  await store.initializeAttributes();
  const attr = store.getAttribute(viewAttribute);

  if (!attr) {
    alerts.push({
      key: `wrong-attr-${itemId}`,
      showItemId: itemId,
      alert: `Attribute [${viewAttribute}], bound to the ${mgr.name} [${label}], not found in data source ${store.ds}!`,
      itemId,
    });
    return [alerts, autoFixes];
  }
  if (!isLOV && !isNull(attr.maxLength) && attr.maxLength !== maxLength) {
    const fix = () => {
      const _page = { ...platform.getPage(appUid, pageId) };
      const _item = _page[itemId] as Item<'StringColumn'>;
      const updatedItem: Item<'StringColumn'> = {
        ..._item,
        props: { ..._item.props, maxLength: attr.maxLength },
      };
      _page[_item.itemId] = updatedItem;
      platform.setter(pageAtom, _page, {
        params: { appUid, pageId },
      });
    };
    autoFixes.push(fix);
    alerts.push({
      key: `maxLength-${itemId}`,
      fix,
      showItemId: itemId,
      alert: `${mgr.name} [${label}]'s Max Length [${maxLength}] is not in sync with the Attribute's Max Length [${attr.maxLength}] in data source ${store.ds}!`,
      itemId,
      severity: 'warning',
    });
  }
  let readonly = store.isReadonly() || isLOV;
  if (!readonly) {
    readonly =
      insertable.type === 'boolean' &&
      !insertable.value &&
      updatable.type === 'boolean' &&
      !updatable.value;
  }
  if (!readonly && attr.allowBlank === 'N' && !required) {
    const fix = () => {
      const _page = { ...platform.getPage(appUid, pageId) };
      const _item = _page[itemId] as Item<'StringColumn'>;
      const updatedItem: Item<'StringColumn'> = {
        ..._item,
        props: { ..._item.props, required: true },
      };
      _page[_item.itemId] = updatedItem;
      platform.setter(pageAtom, _page, {
        params: { appUid, pageId },
      });
    };
    autoFixes.push(fix);
    alerts.push({
      key: `allowBlank-${itemId}`,
      fix,
      showItemId: itemId,
      alert: `${mgr.name} [${label}]'s is not marked as required, but the Attribute [${attr.attributeName}] in ${store.ds} is not optional!`,
      itemId,
      severity: 'warning',
    });
  }

  return [alerts, autoFixes];
}
